import {
  Badge,
  Button,
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
  Input,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@devhop/ui";
import { Link } from "@tanstack/react-router";
import { ExternalLinkIcon, RotateCcwIcon, XIcon } from "lucide-react";
import { useEffect, useState } from "react";
import type {
  InstructorMonthlyPayoutLineDTO,
  InstructorSessionByInstructorItemDTO,
} from "../api/client";
import {
  useDeleteInstructorPayoutLineOverrideMutation,
  useUpsertInstructorPayoutLineOverrideMutation,
} from "../api/queries";
import {
  formatHours,
  formatLinePayoutFormula,
  formatPayoutKip,
  payoutKindLabel,
  sessionLocationLabel,
  sourceTypeLabel,
} from "../lib/instructor-payout-display";
import { PayoutQuotaSummaryStrip } from "./instructor-payout-quota-display";

type InstructorMonthlyPayoutDetailDrawerProps = {
  open: boolean;
  onClose: () => void;
  instructorId: string | null;
  instructorName: string;
  run: { year: number; month: number } | null;
  summaryRow: InstructorSessionByInstructorItemDTO | null;
  lines: InstructorMonthlyPayoutLineDTO[];
  isLoading: boolean;
  canOverride: boolean;
};

function LocationBadge({
  sessionKind,
  payoutLocation,
}: {
  sessionKind: InstructorMonthlyPayoutLineDTO["sessionLocationKind"];
  payoutLocation: InstructorMonthlyPayoutLineDTO["payoutLocationKind"];
}) {
  const label = sessionLocationLabel(sessionKind, payoutLocation);
  const isNear = payoutLocation === "off_site_near";
  const isFar = payoutLocation === "off_site_far";

  if (sessionKind === "studio") {
    return (
      <Badge variant="secondary" className="text-[10px]">
        {label}
      </Badge>
    );
  }
  if (isNear) {
    return (
      <Badge
        variant="outline"
        className="border-sky-500/40 bg-sky-500/10 text-[10px] text-sky-800"
      >
        {label}
      </Badge>
    );
  }
  if (isFar) {
    return (
      <Badge
        variant="outline"
        className="border-violet-500/40 bg-violet-500/10 text-[10px] text-violet-800"
      >
        {label}
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="text-[10px]">
      {label}
    </Badge>
  );
}

function SourceLink({ line }: { line: InstructorMonthlyPayoutLineDTO }) {
  const label = sourceTypeLabel(line.sourceType);
  if (line.sourceType === "class_session") {
    return (
      <Link
        to="/app/classes/sessions/$id"
        params={{ id: line.sourceId }}
        className="inline-flex items-center gap-1 text-primary text-sm hover:underline"
        onClick={(e) => e.stopPropagation()}
      >
        {label}
        <ExternalLinkIcon className="size-3.5" aria-hidden />
      </Link>
    );
  }
  if (line.sourceType === "room_booking") {
    return (
      <Link
        to="/app/room-bookings/$id"
        params={{ id: line.sourceId }}
        className="inline-flex items-center gap-1 text-primary text-sm hover:underline"
        onClick={(e) => e.stopPropagation()}
      >
        {label}
        <ExternalLinkIcon className="size-3.5" aria-hidden />
      </Link>
    );
  }
  return (
    <Link
      to="/app/private-lessons/bookings/$id"
      params={{ id: line.sourceId }}
      className="inline-flex items-center gap-1 text-primary text-sm hover:underline"
      onClick={(e) => e.stopPropagation()}
    >
      {label}
      <ExternalLinkIcon className="size-3.5" aria-hidden />
    </Link>
  );
}

function LinePayoutCell({
  line,
  canOverride,
  instructorId,
  run,
}: {
  line: InstructorMonthlyPayoutLineDTO;
  canOverride: boolean;
  instructorId: string;
  run: { year: number; month: number };
}) {
  const upsertMutation = useUpsertInstructorPayoutLineOverrideMutation();
  const deleteMutation = useDeleteInstructorPayoutLineOverrideMutation();
  const [draft, setDraft] = useState(String(line.linePayoutKip));

  useEffect(() => {
    setDraft(String(line.linePayoutKip));
  }, [line.linePayoutKip, line.sourceId, line.sourceType]);

  const pending = upsertMutation.isPending || deleteMutation.isPending;
  const draftAmount = Number(draft.replace(/[^\d]/g, ""));
  const dirty =
    Number.isFinite(draftAmount) && draftAmount !== line.linePayoutKip;

  const save = () => {
    if (!Number.isFinite(draftAmount) || draftAmount < 0) return;
    upsertMutation.mutate({
      year: run.year,
      month: run.month,
      instructorId,
      sourceType: line.sourceType,
      sourceId: line.sourceId,
      amountKip: draftAmount,
    });
  };

  const reset = () => {
    deleteMutation.mutate({
      year: run.year,
      month: run.month,
      instructorId,
      sourceType: line.sourceType,
      sourceId: line.sourceId,
    });
  };

  if (!canOverride) {
    return (
      <div className="flex flex-col items-end gap-0.5">
        {line.isRetainerFreeSession && line.grossLinePayoutKip > 0 ? (
          <span className="text-muted-foreground text-xs line-through">
            {line.grossLinePayoutKip.toLocaleString("lo-LA")} ₭
          </span>
        ) : null}
        {line.isAmountOverridden &&
        line.calculatedLinePayoutKip !== line.linePayoutKip ? (
          <span className="text-muted-foreground text-xs line-through">
            {line.calculatedLinePayoutKip.toLocaleString("lo-LA")} ₭
          </span>
        ) : null}
        <span
          className={
            line.isAmountOverridden
              ? "text-amber-700 dark:text-amber-300"
              : line.isRetainerFreeSession
                ? "text-emerald-700 dark:text-emerald-300"
                : undefined
          }
        >
          {line.linePayoutKip.toLocaleString("lo-LA")} ₭
        </span>
        {line.isAmountOverridden ? (
          <Badge
            variant="outline"
            className="border-amber-500/40 bg-amber-500/10 text-[10px] text-amber-800 dark:text-amber-200"
          >
            ແກ້ຍອດ
          </Badge>
        ) : null}
      </div>
    );
  }

  return (
    <div className="flex flex-col items-end gap-1.5">
      {line.isAmountOverridden ? (
        <span className="text-muted-foreground text-xs line-through">
          ຄິດໄລ່ {line.calculatedLinePayoutKip.toLocaleString("lo-LA")} ₭
        </span>
      ) : line.isRetainerFreeSession && line.grossLinePayoutKip > 0 ? (
        <span className="text-muted-foreground text-xs line-through">
          {line.grossLinePayoutKip.toLocaleString("lo-LA")} ₭
        </span>
      ) : null}
      <div className="flex items-center justify-end gap-1.5">
        <Input
          type="text"
          inputMode="numeric"
          value={draft}
          disabled={pending}
          className="h-8 w-[7.5rem] text-right tabular-nums"
          aria-label="ຍອດຄ່າຈ້າງ"
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              save();
            }
          }}
        />
        <span className="text-muted-foreground text-xs">₭</span>
      </div>
      <div className="flex items-center gap-1">
        <Button
          type="button"
          size="sm"
          variant="secondary"
          className="h-7 px-2 text-xs"
          disabled={pending || !dirty}
          onClick={save}
        >
          ບັນທຶກ
        </Button>
        {line.isAmountOverridden ? (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="h-7 px-2 text-xs"
            disabled={pending}
            onClick={reset}
            title="ຄືນຍອດຕາມການຄິດໄລ່"
          >
            <RotateCcwIcon className="size-3.5" />
          </Button>
        ) : null}
      </div>
      {line.isAmountOverridden ? (
        <Badge
          variant="outline"
          className="border-amber-500/40 bg-amber-500/10 text-[10px] text-amber-800 dark:text-amber-200"
        >
          ແກ້ຍອດ
        </Badge>
      ) : null}
    </div>
  );
}

export function InstructorMonthlyPayoutDetailDrawer({
  open,
  onClose,
  instructorId,
  instructorName,
  run,
  summaryRow,
  lines,
  isLoading,
  canOverride,
}: InstructorMonthlyPayoutDetailDrawerProps) {
  const lineSum = lines.reduce((a, r) => a + r.linePayoutKip, 0);
  const summaryPayout = summaryRow?.estimatedPayout ?? 0;
  const delta = summaryPayout - lineSum;
  const hasDelta = summaryRow != null && Math.abs(delta) > 0;
  const overrideCount = lines.filter((line) => line.isAmountOverridden).length;

  return (
    <Drawer
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) onClose();
      }}
      direction="right"
    >
      <DrawerContent className="flex h-dvh max-h-dvh !w-[min(98vw,1600px)] !max-w-[min(98vw,1600px)] flex-col overflow-hidden p-0">
        <DrawerHeader className="shrink-0 border-b px-5 pb-4 pt-5 sm:px-6">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 text-left">
              <DrawerTitle className="text-xl">{instructorName}</DrawerTitle>
              {run ? (
                <DrawerDescription className="text-sm tabular-nums">
                  ເດືອນ {run.month}/{run.year}
                </DrawerDescription>
              ) : null}
            </div>
            <DrawerClose
              className="rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-hidden focus:ring-2 focus:ring-ring focus:ring-offset-2"
              aria-label="ປິດ"
            >
              <XIcon className="size-4" />
            </DrawerClose>
          </div>
        </DrawerHeader>

        {summaryRow ? (
          <>
            {(summaryRow.monthlyFreeGeneralClassSessions ?? 0) > 0 ? (
              <div className="shrink-0 border-b px-5 py-3 sm:px-6">
                <PayoutQuotaSummaryStrip row={summaryRow} />
              </div>
            ) : null}
            <div className="grid shrink-0 grid-cols-3 gap-3 border-b bg-muted/20 px-5 py-4 sm:px-6">
              <div className="rounded-lg border bg-background px-4 py-3 shadow-sm">
                <div className="text-muted-foreground text-xs">ຄັ້ງ</div>
                <div className="font-semibold text-lg tabular-nums">
                  {summaryRow.sessionsTaught.toLocaleString("lo-LA")}
                </div>
              </div>
              <div className="rounded-lg border bg-background px-4 py-3 shadow-sm">
                <div className="text-muted-foreground text-xs">ຊົ່ວໂມງ</div>
                <div className="font-semibold text-lg tabular-nums">
                  {formatHours(summaryRow.billableHoursTotal)}
                </div>
              </div>
              <div className="rounded-lg border bg-background px-4 py-3 shadow-sm">
                <div className="text-muted-foreground text-xs">ຄ່າຈ້າງສະຫຼຸບ</div>
                <div className="font-semibold text-lg text-red-700 tabular-nums dark:text-red-400">
                  {formatPayoutKip(summaryRow.estimatedPayout)}
                </div>
              </div>
            </div>
          </>
        ) : null}

        <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden">
          <div className="px-5 py-5 sm:px-6">
            {isLoading ? (
              <p className="py-12 text-center text-muted-foreground text-sm">
                ກຳລັງໂຫລດ...
              </p>
            ) : lines.length === 0 ? (
              <p className="py-12 text-center text-muted-foreground text-sm">
                ບໍ່ມີບັນທັດໃນຊ່ວງນີ້
              </p>
            ) : (
              <>
                {canOverride ? (
                  <div className="mb-4 rounded-md border bg-muted/30 px-3 py-2 text-muted-foreground text-xs leading-relaxed">
                    ແກ້ຍອດຄ່າຈ້າງຕໍ່ບັນທັດໄດ້ກ່ອນຢືນຢັນຮອບຈ່າຍ
                    {overrideCount > 0
                      ? ` · ມີ ${overrideCount} ບັນທັດທີ່ແກ້ຍອດແລ້ວ`
                      : ""}
                  </div>
                ) : null}
                {hasDelta ? (
                  <div className="mb-4 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-amber-900 text-xs leading-relaxed dark:text-amber-200">
                    ຜົນລວມບັນທັດ ({formatPayoutKip(lineSum)}) ຕ່າງຈາກສະຫຼຸບຕາຕະລາງ (
                    {formatPayoutKip(summaryPayout)}) ປະມານ{" "}
                    {Math.abs(delta).toLocaleString("lo-LA")} ₭ — ອາດເກີດຈາກການຄິດແບບ
                    ລວມຕໍ່ kind vs ປັດທີລະຄັ້ງ
                  </div>
                ) : null}

                <div className="overflow-x-auto rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="min-w-[120px]">ວັນທີ່</TableHead>
                        <TableHead className="min-w-[280px]">ແຫຼ່ງ</TableHead>
                        <TableHead className="min-w-[120px]">ສະຖານທີ່</TableHead>
                        <TableHead className="min-w-[180px]">ປະເພດຄ່າຈ້າງ</TableHead>
                        <TableHead className="min-w-[120px] text-right">
                          ຄ່າຈ້າງ
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {lines.map((line) => (
                        <TableRow
                          key={`${line.sourceType}-${line.sourceId}`}
                          className={
                            line.isRetainerFreeSession
                              ? "bg-emerald-500/5 hover:bg-emerald-500/10"
                              : undefined
                          }
                        >
                          <TableCell className="whitespace-nowrap tabular-nums text-sm">
                            <div>{line.date}</div>
                            {line.startTime && line.endTime ? (
                              <div className="text-muted-foreground text-xs">
                                {String(line.startTime).slice(0, 5)}–
                                {String(line.endTime).slice(0, 5)}
                              </div>
                            ) : null}
                          </TableCell>
                          <TableCell className="text-sm">
                            <div className="space-y-0.5">
                              <SourceLink line={line} />
                              {line.sourceType === "class_session" &&
                                line.className ? (
                                <div className="font-medium text-foreground text-sm">
                                  {line.className}
                                </div>
                              ) : null}
                              {line.sourceType === "private_booking" &&
                                line.studentName ? (
                                <div className="font-medium text-foreground text-sm">
                                  {line.studentName}
                                </div>
                              ) : null}
                              {line.engagementKind ===
                                "partner_instructor_only" ? (
                                <span className="text-muted-foreground text-xs">
                                  Partner
                                </span>
                              ) : null}
                              {line.sourceType === "room_booking" ? (
                                <div className="text-muted-foreground text-xs">
                                  {[line.roomName, line.bookingLabel]
                                    .filter(Boolean)
                                    .join(" · ")}
                                </div>
                              ) : null}
                            </div>
                          </TableCell>
                          <TableCell>
                            <LocationBadge
                              sessionKind={line.sessionLocationKind}
                              payoutLocation={line.payoutLocationKind}
                            />
                          </TableCell>
                          <TableCell className="text-sm">
                            <div className="flex flex-wrap items-center gap-1.5">
                              {payoutKindLabel(
                                line.payoutWorkKind,
                                line.payoutLocationKind,
                              )}
                              {line.monthGeneralClassLeadIndex != null ? (
                                <Badge variant="outline" className="text-[10px] tabular-nums">
                                  #{line.monthGeneralClassLeadIndex}/
                                  {summaryRow?.monthlyFreeGeneralClassSessions ?? "—"}
                                </Badge>
                              ) : null}
                              {line.isRetainerFreeSession ? (
                                <Badge
                                  variant="outline"
                                  className="border-emerald-500/40 bg-emerald-500/10 text-[10px] text-emerald-800 dark:text-emerald-200"
                                >
                                  Retainer
                                </Badge>
                              ) : null}
                            </div>
                          </TableCell>
                          <TableCell
                            className="text-right font-medium tabular-nums text-sm"
                            title={formatLinePayoutFormula(line)}
                          >
                            {instructorId && run ? (
                              <LinePayoutCell
                                line={line}
                                canOverride={canOverride}
                                instructorId={instructorId}
                                run={run}
                              />
                            ) : (
                              <span>{line.linePayoutKip.toLocaleString("lo-LA")} ₭</span>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                <div className="mt-3 flex justify-between border-t pt-3 text-sm">
                  <span className="text-muted-foreground">
                    ຜົນລວມບັນທັດ (ປັດທີລະຄັ້ງ)
                  </span>
                  <span className="font-medium tabular-nums">
                    {lineSum.toLocaleString("lo-LA")} ₭
                  </span>
                </div>
                <p className="mt-2 pb-2 text-muted-foreground text-xs leading-relaxed">
                  ເລື່ອນເມົ້າເທິງຍອດແຕ່ລະແຖວເພື່ອເບິ່ງສູດຄິດໄລ່ · ຄລິກແຫຼ່ງເພື່ອເປີດ ຊົ່ວໂມງຮຽນ /
                  booking
                </p>
              </>
            )}
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
